import PageTemplate from "components/PageTemplate";
import classes from "./classes.module.scss";
import I18n from "components/materials/I18n";
import Typography from "components/elements/Typography";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import { useState, useCallback, useEffect, useMemo } from "react";
import { container } from "tsyringe";
import ProjectService from "services/ProjectService";
import ProjectGrid from "./components/ProjectList/ProjectGrid";
import SearchBar from "./components/SearchBar";
import FilterButton from "./components/Filter/FilterButton";
import FilterModal from "./components/Filter/FilterModal";
import Tag, { EProjectSortOption, ETagVariant } from "./components/Tag";
import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import { getProjectLeaderName, getPublicationDate } from "./components/ProjectList/ProjectCardFactory";

const projectService = container.resolve(ProjectService);

const customOrder = ["Trainee", "ClubVip", "Potus", "RajeunirOuMourir", "BataillonSilencieux", "SnowVision", "Dim", "GrandSaut", "MalgreMoi", "Friends", "KDanse", "LAttente"];

const sortFeaturedProjects = (projects: ProjectResponseResource[]) => {
	return projects.sort((a, b) => {
		const indexA = a.componentName ? customOrder.indexOf(a.componentName) : -1;
		const indexB = b.componentName ? customOrder.indexOf(b.componentName) : -1;

		if (indexA !== -1 && indexB !== -1) {
			return indexA - indexB;
		}
		if (indexA !== -1) {
			return -1;
		}
		if (indexB !== -1) {
			return 1;
		}
		if (a.featured && !b.featured) {
			return -1;
		}
		if (!a.featured && b.featured) {
			return 1;
		}
		return 0;
	});
};

function filterUniqueById<T extends { id: string }>(arr: T[]): T[] {
	return arr.reduce<T[]>((acc, item) => (acc.some((existing) => existing.id === item.id) ? acc : [...acc, item]), []);
}

export default function Home() {
	const [featuredProjects, setFeaturedProjects] = useState<ProjectResponseResource[]>([]);
	const [publishedProjects, setPublishedProjects] = useState<ProjectResponseResource[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [selectedFormat, setSelectedFormat] = useState<EProjectFormat | null>(null);
	const [selectedSort, setSelectedSort] = useState<EProjectSortOption | null>(null);
	const [selectedThemes, setSelectedThemes] = useState<EProjectTheme[]>([]);

	const fetchProjects = useCallback(() => {
		projectService.getFeaturedProjects().then((projects) => setFeaturedProjects(sortFeaturedProjects(projects)));
		projectService.getPublishedProjects().then((projects) => setPublishedProjects(projects));
	}, []);

	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	const allProjects = filterUniqueById([...featuredProjects, ...publishedProjects]);

	const filteredProjects = useMemo(() => {
		let filtered = [...allProjects];

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((project) => {
				const title = project.title?.toLowerCase() || "";
				const pitch = project.pitch?.toLowerCase() || "";
				const projectLeaderName = getProjectLeaderName(project).toLowerCase();

				return title.includes(query) || pitch.includes(query) || projectLeaderName.includes(query);
			});
		}

		// Apply format filter
		if (selectedFormat) {
			filtered = filtered.filter((project) => project.format === selectedFormat);
		}

		// Apply theme filter
		if (selectedThemes.length > 0) {
			filtered = filtered.filter((project) => selectedThemes.includes(project.theme));
		}

		// Apply sort
		if (selectedSort) {
			filtered = [...filtered].sort((a, b) => {
				switch (selectedSort) {
					case EProjectSortOption.NEWEST:
						return new Date(getPublicationDate(b)).getTime() - new Date(getPublicationDate(a)).getTime();
					case EProjectSortOption.OLDEST:
						return new Date(getPublicationDate(a)).getTime() - new Date(getPublicationDate(b)).getTime();
					case EProjectSortOption.MOST_LIKED:
						return (b.likes?.length || 0) - (a.likes?.length || 0);
					case EProjectSortOption.LEAST_LIKED:
						return (a.likes?.length || 0) - (b.likes?.length || 0);
					default:
						return 0;
				}
			});
		}

		return filtered;
	}, [allProjects, searchQuery, selectedFormat, selectedThemes, selectedSort]);

	const handleApplyFilters = useCallback((format: EProjectFormat | null, sort: EProjectSortOption | null, themes: EProjectTheme[]) => {
		setSelectedFormat(format);
		setSelectedSort(sort);
		setSelectedThemes(themes);
	}, []);

	const handleRemoveFormat = useCallback(() => {
		setSelectedFormat(null);
	}, []);

	const handleRemoveSort = useCallback(() => {
		setSelectedSort(null);
	}, []);

	const handleRemoveTheme = useCallback((theme: EProjectTheme) => {
		setSelectedThemes((prev) => prev.filter((t) => t !== theme));
	}, []);

	return (
		<PageTemplate tabTitle={I18n.trslt(I18n.asset.pages.projects.page_title)} mainSectionClassName={classes["root"]}>
			<div className={classes["header"]}>
				<div className={classes["title-container"]}>
					<Typography type="h1" weight="bold">
						{I18n.trslt(I18n.asset.pages.projects.title)}
						<Typography type="span" weight="bold" color="secondary" className={classes["inside-title"]}>
							&nbsp;{I18n.trslt(I18n.asset.pages.projects.inside_title)}
						</Typography>
					</Typography>

					<Typography type="p" size="xlarge" weight="regular">
						{I18n.trslt(I18n.asset.pages.projects.subtitle)}
					</Typography>
				</div>

				<div className={classes["controls-container"]}>
					<div className={classes["search-filter-container"]}>
						<SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
						<FilterButton onClick={() => setIsFilterModalOpen(true)} />
					</div>
					{(selectedSort || selectedFormat || selectedThemes.length > 0) && (
						<div className={classes["tags-container"]}>
							{selectedSort && <Tag value={{ variant: ETagVariant.SORT, label: selectedSort }} onClick={handleRemoveSort} />}
							{selectedFormat && <Tag value={{ variant: ETagVariant.FORMAT, label: selectedFormat }} onClick={handleRemoveFormat} />}
							{selectedThemes.map((theme) => (
								<Tag key={theme} value={{ variant: ETagVariant.THEME, label: theme }} onClick={() => handleRemoveTheme(theme)} />
							))}
						</div>
					)}
				</div>
			</div>

			<div className={classes["body-container"]}>
				<ProjectGrid projects={filteredProjects} />
			</div>

			<FilterModal
				isOpen={isFilterModalOpen}
				onClose={() => setIsFilterModalOpen(false)}
				selectedFormat={selectedFormat}
				selectedSort={selectedSort}
				selectedThemes={selectedThemes}
				onApply={handleApplyFilters}
				allProjects={allProjects}
				searchQuery={searchQuery}
			/>
		</PageTemplate>
	);
}
